/**
 * File Attachment Service
 * Handles file upload, download, and management for KATA Board v2
 */

import apiClient from './api';
import type { FileAttachment, FileAttachmentCreate, FileAttachmentFilters } from '../types/kataBoardV2';

export const FileAttachmentService = {
  /**
   * Get all file attachments with optional filters
   */
  async getAll(filters?: FileAttachmentFilters): Promise<FileAttachment[]> {
    const params = new URLSearchParams();
    
    if (filters?.kata_board) {
      params.append('kata_board', filters.kata_board.toString());
    }
    if (filters?.pdca_cycle) {
      params.append('pdca_cycle', filters.pdca_cycle.toString());
    }
    if (filters?.attachment_type) {
      params.append('attachment_type', filters.attachment_type);
    }
    if (filters?.is_before_image !== undefined) {
      params.append('is_before_image', filters.is_before_image.toString());
    }
    if (filters?.is_after_image !== undefined) {
      params.append('is_after_image', filters.is_after_image.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `/file-attachments/?${queryString}` : '/file-attachments/';
    
    const response = await apiClient.get(url);
    
    // Handle paginated response (results property) or direct array
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      return response.data.results;
    }
    
    // Handle direct array response (fallback)
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Get a specific file attachment by ID
   */
  async getById(id: number): Promise<FileAttachment> {
    const response = await apiClient.get(`/file-attachments/${id}/`);
    return response.data;
  },

  /**
   * Create a new file attachment
   */
  async create(data: FileAttachmentCreate): Promise<FileAttachment> {
    const formData = new FormData();
    
    formData.append('kata_board', data.kata_board.toString());
    formData.append('attachment_type', data.attachment_type);
    if (data.pdca_cycle !== undefined) formData.append('pdca_cycle', data.pdca_cycle.toString());
    formData.append('file', data.file);
    
    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.is_before_image !== undefined) {
      formData.append('is_before_image', data.is_before_image.toString());
    }
    if (data.is_after_image !== undefined) {
      formData.append('is_after_image', data.is_after_image.toString());
    }

    const response = await apiClient.post('/file-attachments/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  /**
   * Update a file attachment (metadata only, not the file itself)
   */
  async update(id: number, data: Partial<Omit<FileAttachmentCreate, 'file' | 'kata_board'>>): Promise<FileAttachment> {
    const response = await apiClient.patch(`/file-attachments/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a file attachment
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/file-attachments/${id}/`);
  },

  /**
   * Get file attachments by KATA Board ID
   */
  async getByKataBoard(kataBoardId: number): Promise<FileAttachment[]> {
    return this.getAll({ kata_board: kataBoardId });
  },

  /**
   * Get before images for a KATA Board
   */
  async getBeforeImages(kataBoardId: number): Promise<FileAttachment[]> {
    return this.getAll({ 
      kata_board: kataBoardId, 
      is_before_image: true 
    });
  },

  /**
   * Get after images for a KATA Board
   */
  async getAfterImages(kataBoardId: number): Promise<FileAttachment[]> {
    return this.getAll({ 
      kata_board: kataBoardId, 
      is_after_image: true 
    });
  },

  /**
   * Upload multiple files at once
   */
  async uploadMultiple(files: FileAttachmentCreate[]): Promise<FileAttachment[]> {
    const uploadPromises = files.map(file => this.create(file));
    return Promise.all(uploadPromises);
  },

  /**
   * Delete multiple file attachments
   */
  async deleteMultiple(ids: number[]): Promise<void> {
    const deletePromises = ids.map(id => this.delete(id));
    await Promise.all(deletePromises);
  },

  /**
   * Validate file before upload
   */
  validateFile(file: File): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (10MB)`);
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type "${file.type}" is not supported`);
    }

    // Check file name
    if (!file.name || file.name.trim() === '') {
      errors.push('File name is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Check if file is an image
   */
  isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  },

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  },

  /**
   * Generate thumbnail URL for images (if supported by backend)
   */
  getThumbnailUrl(attachment: FileAttachment, size: 'small' | 'medium' | 'large' = 'medium'): string {
    if (!this.isImageFile(attachment.mime_type)) {
      return attachment.file_url;
    }
    
    // If backend supports thumbnails, modify URL accordingly
    // For now, return original URL
    return attachment.file_url;
  }
};

export default FileAttachmentService;


